import { ElementJsonData } from '../models/element-types';
import { DomUtils } from '../utils/dom-utils';

export abstract class RendererBase<TData extends ElementJsonData, TOptions> {
    public render(data: TData, options?: TOptions): HTMLElement {
        const element = this.renderElement(data, options);
        element.dataset['elementJson'] = JSON.stringify(data);
        return element;
    }

    public getData(element: HTMLElement): TData | null {
        const jsonData = element.dataset['elementJson'];
        if (!jsonData) {
            return null;
        }

        try {
            return JSON.parse(jsonData) as TData;
        } catch (e) {
            console.error('Failed to parse element data:', e);
            return null;
        }
    }

    public updateData(element: HTMLElement, data: TData): void {
        element.dataset['elementJson'] = JSON.stringify(data);
    }

    public applyData(element: HTMLElement, data: TData): void {
        this.updateData(element, data);

        this.applyVisualProperties(element, data);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected applyVisualProperties(element: HTMLElement, data: TData): void {
    }

    public createNew(options?: TOptions): HTMLElement {
        const defaultData = this.createDefaultData();
        return this.render(defaultData, options);
    }

    public createWithData(data: TData, options?: TOptions): HTMLElement {
        const clonedData = { ...data, id: DomUtils.generateUniqueElementId([]) };
        return this.render(clonedData, options);
    }

    public resetToDefault(element: HTMLElement): void {
        const currentData = this.getData(element);
        if (!currentData) {
            return;
        }

        const defaultData = { ...this.defaultData, id: currentData.id };
        this.applyData(element, defaultData);
    }

    protected createDefaultData(): TData {
        const defaultData = this.defaultData;
        defaultData.id = DomUtils.generateUniqueElementId([]);

        return defaultData;
    }

    protected abstract get defaultData(): TData;

    protected abstract renderElement(data: TData, options?: TOptions): HTMLElement;

    protected abstract applyCustomCssClasses(element: HTMLElement, cssClasses?: string[], options?: TOptions): void;
}
